/**
 * Sector Rankings Widget — RS vs market scoring
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, alpha, Tooltip, IconButton, Skeleton,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, InfoOutlined, Leaderboard,
  LocalFireDepartment, AccountBalance, ShoppingCart, Factory,
  Construction, HealthAndSafety, Computer, Wifi, Store,
  ElectricBolt, Home,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tradingDecisionAPI } from '../../services/api';

const SECTOR_ICON_KEYWORDS = [
  [/energy|năng lượng|dầu|khí/i, LocalFireDepartment],
  [/financ|tài chính|ngân hàng|bank/i, AccountBalance],
  [/staple|tiêu dùng(?!.*thiết)/i, ShoppingCart],
  [/industrial|công nghiệp/i, Factory],
  [/material|vật liệu|nguyên liệu/i, Construction],
  [/health|sức khỏe|y tế|dược/i, HealthAndSafety],
  [/tech|công nghệ|thông tin/i, Computer],
  [/comm|viễn thông|truyền thông/i, Wifi],
  [/discret|thiết yếu|bán lẻ/i, Store],
  [/utilit|tiện ích|điện|nước/i, ElectricBolt],
  [/real.?estate|bất động sản|nhà đất/i, Home],
];
const getSectorIcon = name => {
  const s = typeof name === 'object' ? `${name?.vi || ''} ${name?.en || ''}` : (name || '');
  return (SECTOR_ICON_KEYWORDS.find(([re]) => re.test(s)) || [])[1] || Factory;
};

const clrScore = s => s >= 120 ? '#10b981' : s >= 105 ? '#22c55e' : s >= 95 ? '#f59e0b' : s >= 85 ? '#f97316' : '#ef4444';

const SectorRankingsWidget = ({ calcDate = null }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rankings, setRankings] = useState([]);

  const sectorName = s => (typeof s === 'object' && s?.en && s?.vi) ? (i18n.language === 'vi' ? s.vi : s.en) : s || 'N/A';

  useEffect(() => { fetchData(); }, [calcDate]); // eslint-disable-line

  const fetchData = async () => {
    try {
      setLoading(true); setError(null);
      const r = await tradingDecisionAPI.getSectorRankings(calcDate);
      if (r.data?.message?.includes('not available')) { setRankings([]); return; }
      setRankings(r.data?.rankings || (Array.isArray(r.data) ? r.data : []));
    } catch (e) { setError(e.message || 'Failed'); setRankings([]); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', height: '100%', overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="rounded" width={26} height={26} sx={{ borderRadius: 1 }} animation="wave" />
          <Skeleton width={110} height={16} animation="wave" />
        </Box>
        <Box sx={{ px: 1.5, pt: 1, pb: 1 }}>
          {[0,1,2,3,4,5,6,7,8,9,10].map(i => <Skeleton key={i} height={32} sx={{ mb: 0.3 }} animation="wave" />)}
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2, height: '100%' }}>
        <Typography sx={{ fontSize: 12, color: '#ef4444' }}>{error}</Typography>
      </Box>
    );
  }

  const maxScore = rankings.length > 0 ? Math.max(...rankings.map(r => r.combined_score || r.score || 0)) : 150;

  return (
    <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, py: 0.8, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: 1, background: 'linear-gradient(135deg, #9333ea, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Leaderboard sx={{ fontSize: 14, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
              {t('tradingDecision.sectorRankings.title', 'Sức Mạnh Ngành')}
            </Typography>
            <Typography sx={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>
              {t('tradingDecision.sectorRankings.basedOn60Days', 'RS 60 phiên · Baseline = 100')}
            </Typography>
          </Box>
        </Box>
        <Tooltip title={t('tradingDecision.sectorRankings.scoreFormula', 'Score = Relative Strength 60d vs VN-Index. 100 = market average')}>
          <IconButton size="small" sx={{ width: 22, height: 22 }}><InfoOutlined sx={{ fontSize: 13, color: '#94a3b8' }} /></IconButton>
        </Tooltip>
      </Box>

      {/* Sector List */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 0.5 }}>
        {rankings.map((item, idx) => {
          const score = item.combined_score || item.score || 0;
          const c = clrScore(score);
          const vs = score - 100;
          const barW = Math.min((score / maxScore) * 100, 100);
          const SIcon = getSectorIcon(item.sector);
          const isTop3 = idx < 3;

          return (
            <Box key={item.sector || idx} sx={{
              display: 'flex', alignItems: 'center', py: 0.55,
              borderBottom: idx < rankings.length - 1 ? '1px solid #f8fafc' : 'none',
            }}>
              {/* Rank */}
              <Typography sx={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0, mr: 0.6,
                bgcolor: isTop3 ? (idx === 0 ? '#fbbf24' : idx === 1 ? '#a1a1aa' : '#d97706') : alpha('#94a3b8', 0.08),
                color: isTop3 ? '#fff' : '#94a3b8',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800,
              }}>
                {idx + 1}
              </Typography>

              {/* Icon */}
              <SIcon sx={{ fontSize: 14, color: '#94a3b8', mr: 0.5, flexShrink: 0 }} />

              {/* Name */}
              <Typography sx={{
                flex: 1, minWidth: 0, fontSize: 12, fontWeight: isTop3 ? 600 : 500,
                color: isTop3 ? '#1e293b' : '#475569',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {sectorName(item.sector)}
              </Typography>

              {/* Bar */}
              <Box sx={{ width: 60, mx: 0.8, flexShrink: 0 }}>
                <Box sx={{ height: 4, borderRadius: 2, bgcolor: alpha(c, 0.1) }}>
                  <Box sx={{ height: '100%', borderRadius: 2, bgcolor: c, width: `${barW}%`, transition: 'width 0.4s' }} />
                </Box>
              </Box>

              {/* Score */}
              <Typography sx={{
                fontSize: 12, fontWeight: 800, color: c, fontFamily: 'monospace',
                minWidth: 34, textAlign: 'right',
              }}>
                {score.toFixed(1)}
              </Typography>

              {/* Change vs market */}
              <Typography sx={{
                fontSize: 10, fontWeight: 600, minWidth: 38, textAlign: 'right', ml: 0.3,
                color: vs >= 0 ? '#10b981' : '#ef4444',
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.15,
              }}>
                {vs >= 0 ? <TrendingUp sx={{ fontSize: 10 }} /> : <TrendingDown sx={{ fontSize: 10 }} />}
                {vs >= 0 ? '+' : ''}{vs.toFixed(1)}%
              </Typography>
            </Box>
          );
        })}
      </Box>

      {rankings.length === 0 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Leaderboard sx={{ fontSize: 24, color: '#cbd5e1', mb: 0.5 }} />
          <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{t('tradingDecision.sectorRankings.noData', 'Không có dữ liệu')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default SectorRankingsWidget;
